import { type ButtonHTMLAttributes, type FC, type Ref } from "react";
import { baseAttrs } from "../utils";

export type _BaseButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	/**
	 * Test selector hook. Expanded by `baseAttrs` into a `data-testid` attribute.
	 */
	testId?: string;

	/**
	 * Analytics identifier. Expanded by `baseAttrs` into both `data-pendo-id` and
	 * `data-amp-track-id` attributes on the underlying `<button>`, so a single value
	 * flows to both Pendo and Amplitude pipelines.
	 */
	trackingId?: string;

	ref?: Ref<HTMLButtonElement>;
};

/**
 * Internal primitive base for buttons. A drop-in replacement for the native
 * `<button>` that additionally understands our proprietary `testId`/`trackingId`
 * props (expanded via `baseAttrs`) and defaults `type` to `"button"` (the native
 * default is `"submit"`).
 *
 * Intended for use **only within `primitives/`** — higher-level buttons (e.g.
 * `Button`) build on this so the tracking/analytics wiring lives in one place.
 * Do not export it from the primitives layer or consume it from feature code.
 */
export const _BaseButton: FC<_BaseButtonProps> = ({
	id,
	testId,
	trackingId,
	className,
	type = "button",
	ref,
	...rest
}) => (
	<button ref={ref} type={type} {...baseAttrs({ id, testId, trackingId, className })} {...rest} />
);
_BaseButton.displayName = "_BaseButton";
